import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import {
    createCouponService,
    deleteCouponService,
    getCouponsService,
    updateCouponService,
} from '../../services/coupons';
import { formatNumber } from '../../constants.js/misc';
import LimitedFreemiumPlan from '../../components/LimitedFreemiumPlan';
import CouponsPageMobile from './CouponsPageMobile';

const emptyForm = {
    id: null,
    code: '',
    discount_type: 'percentage',
    discount_value: 0,
    max_uses: null,
    min_order_amount: null,
    max_discount_amount: null,
    valid_from: '',
    valid_until: '',
    prevent_reuse_by_phone: false,
    prevent_reuse_by_email: false,
    first_purchase_only: false,
    active: true,
};

const CouponsPage = ({ storeId, storeData }) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [coupons, setCoupons] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(false);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [formValues, setFormValues] = useState(emptyForm);

    const storeCurrency = storeData?.currency || storeData?.store_plan?.currency;
    const couponsFeatureEnabled = storeData?.plan_features?.is_coupons_enabled === true;

    const formatMoneyWithCode = (value) => {
        if (value === null || value === undefined || value === '') return '';
        if (!storeCurrency) return formatNumber(value);
        return `${formatNumber(value, storeCurrency)} ${storeCurrency}`;
    };

    const loadCoupons = () => {
        setLoading(true);
        getCouponsService(storeId, page, rowsPerPage)
            .then((res) => {
                setCoupons(res.data || []);
                const total = +res.headers['x-total-count'] || (res.data || []).length;
                setTotalRows(total);
            })
            .catch(() => {
                enqueueSnackbar('No se pudieron cargar los cupones', {
                    variant: 'error',
                    autoHideDuration: 3000,
                });
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!storeId || !couponsFeatureEnabled) return;
        loadCoupons();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storeId, page, rowsPerPage, couponsFeatureEnabled]);

    const handleChangePage = (_, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(+event.target.value);
        setPage(0);
    };

    const handleOpenDialog = (coupon = null) => {
        if (coupon) {
            setFormValues({
                ...emptyForm,
                ...coupon,
                valid_from: coupon.valid_from ? coupon.valid_from.slice(0, 16) : '',
                valid_until: coupon.valid_until ? coupon.valid_until.slice(0, 16) : '',
            });
        } else {
            setFormValues(emptyForm);
        }
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setFormValues(emptyForm);
    };

    const handleFormChange = (field, value) => {
        setFormValues((prev) => ({
            ...prev,
            [field]: value,
        }));
    };


    const handleSubmit = async () => {
        try {
            if (!formValues.code || !formValues.discount_type || !formValues.discount_value) {
                enqueueSnackbar('Completa los campos obligatorios', {
                    variant: 'warning',
                    autoHideDuration: 3000,
                });
                return;
            }

            const payload = {
                code: formValues.code.trim().toUpperCase(),
                discount_type: formValues.discount_type,
                discount_value: Number(formValues.discount_value),
                max_uses: formValues.max_uses ? Number(formValues.max_uses) : null,
                min_order_amount: formValues.min_order_amount
                    ? Number(formValues.min_order_amount)
                    : null,
                max_discount_amount: formValues.max_discount_amount
                    ? Number(formValues.max_discount_amount)
                    : null,
                valid_from: formValues.valid_from ? new Date(formValues.valid_from).toISOString() : null,
                valid_until: formValues.valid_until
                    ? new Date(formValues.valid_until).toISOString()
                    : null,
                prevent_reuse_by_phone: !!formValues.prevent_reuse_by_phone,
                prevent_reuse_by_email: !!formValues.prevent_reuse_by_email,
                first_purchase_only: !!formValues.first_purchase_only,
                active: !!formValues.active,
            };

            if (formValues.id) {
                await updateCouponService(storeId, formValues.id, payload);
                enqueueSnackbar('Cupón actualizado correctamente', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            } else {
                await createCouponService(storeId, payload);
                enqueueSnackbar('Cupón creado correctamente', {
                    variant: 'success',
                    autoHideDuration: 3000,
                });
            }

            handleCloseDialog();
            loadCoupons();
        } catch (error) {
            enqueueSnackbar('Ocurrió un error guardando el cupón', {
                variant: 'error',
                autoHideDuration: 3000,
            });
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };

    const hasAtLeastOneUse = (coupon) => (coupon?.current_uses ?? 0) >= 1;

    const handleDelete = async (coupon) => {
        if (hasAtLeastOneUse(coupon)) {
            enqueueSnackbar(
                'No se puede eliminar un cupón que ya tiene usos. Puedes desactivarlo desde Editar para que no se siga aplicando.',
                { variant: 'info', autoHideDuration: 5000 }
            );
            return;
        }
        if (!window.confirm(`¿Eliminar el cupón ${coupon.code}?`)) return;

        try {
            await deleteCouponService(storeId, coupon.id);
            enqueueSnackbar('Cupón eliminado correctamente', {
                variant: 'success',
                autoHideDuration: 3000,
            });
            loadCoupons();
        } catch (error) {
            enqueueSnackbar('No se pudo eliminar el cupón', {
                variant: 'error',
                autoHideDuration: 3000,
            });
            // eslint-disable-next-line no-console
            console.error(error);
        }
    };

    if (!storeId) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <Typography variant="h5">Cupones</Typography>
                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                        Selecciona una tienda para ver y administrar cupones.
                    </Typography>
                </Grid>
            </Grid>
        );
    }

    if (!couponsFeatureEnabled) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                width="100%"
                p={2}
                pb={isMobile ? 10 : 2}
            >
                <Box maxWidth={560} width="100%">
                    <LimitedFreemiumPlan type="payment" embedded />
                </Box>
            </Box>
        );
    }

    if (isMobile) {
        return (
            <>
                <CouponsPageMobile
                    storeId={storeId}
                    coupons={coupons}
                    loading={loading}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    totalRows={totalRows}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    onCreate={() => handleOpenDialog()}
                    onEdit={handleOpenDialog}
                    onDelete={handleDelete}
                    hasAtLeastOneUse={hasAtLeastOneUse}
                    formatMoneyWithCode={formatMoneyWithCode}
                />

                <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                    <DialogTitle>{formValues.id ? 'Editar cupón' : 'Crear cupón'}</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Código"
                                    value={formValues.code}
                                    onChange={(e) => handleFormChange('code', e.target.value)}
                                    fullWidth
                                    required
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="discount-type-label">Tipo de descuento</InputLabel>
                                    <Select
                                        labelId="discount-type-label"
                                        value={formValues.discount_type}
                                        onChange={(e) =>
                                            handleFormChange('discount_type', e.target.value)
                                        }
                                    >
                                        <MenuItem value="percentage">Porcentaje</MenuItem>
                                        <MenuItem value="fixed">Monto fijo</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label={
                                        formValues.discount_type === 'percentage'
                                            ? 'Descuento (%)'
                                            : 'Descuento (monto)'
                                    }
                                    type="number"
                                    inputProps={{ min: 0.01, step: 0.01 }}
                                    value={formValues.discount_value}
                                    onChange={(e) => handleFormChange('discount_value', e.target.value)}
                                    fullWidth
                                    required
                                    helperText={
                                        formValues.discount_type === 'fixed' &&
                                        formValues.discount_value
                                            ? formatMoneyWithCode(formValues.discount_value)
                                            : ''
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Usos máximos (opcional)"
                                    type="number"
                                    inputProps={{ min: 1 }}
                                    value={formValues.max_uses || ''}
                                    onChange={(e) =>
                                        handleFormChange('max_uses', e.target.value || null)
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Monto mínimo (opcional)"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={formValues.min_order_amount || ''}
                                    onChange={(e) =>
                                        handleFormChange('min_order_amount', e.target.value || null)
                                    }
                                    fullWidth
                                    helperText={
                                        formValues.min_order_amount
                                            ? formatMoneyWithCode(formValues.min_order_amount)
                                            : ''
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Descuento máximo (opcional)"
                                    type="number"
                                    inputProps={{ min: 0, step: 0.01 }}
                                    value={formValues.max_discount_amount || ''}
                                    onChange={(e) =>
                                        handleFormChange('max_discount_amount', e.target.value || null)
                                    }
                                    fullWidth
                                    disabled={formValues.discount_type !== 'percentage'}
                                    helperText={
                                        formValues.max_discount_amount
                                            ? formatMoneyWithCode(formValues.max_discount_amount)
                                            : ''
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Válido desde (opcional)"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    value={formValues.valid_from}
                                    onChange={(e) => handleFormChange('valid_from', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Válido hasta (opcional)"
                                    type="datetime-local"
                                    InputLabelProps={{ shrink: true }}
                                    value={formValues.valid_until}
                                    onChange={(e) => handleFormChange('valid_until', e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formValues.prevent_reuse_by_phone}
                                            onChange={(e) =>
                                                handleFormChange('prevent_reuse_by_phone', e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Evitar reutilizar por teléfono"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formValues.prevent_reuse_by_email}
                                            onChange={(e) =>
                                                handleFormChange('prevent_reuse_by_email', e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Evitar reutilizar por email"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formValues.first_purchase_only}
                                            onChange={(e) =>
                                                handleFormChange('first_purchase_only', e.target.checked)
                                            }
                                            color="primary"
                                        />
                                    }
                                    label="Solo para primera compra"
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formValues.active}
                                            onChange={(e) => handleFormChange('active', e.target.checked)}
                                            color="primary"
                                        />
                                    }
                                    label="Cupón activo"
                                />
                            </Grid>
                        </Grid>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseDialog}>Cancelar</Button>
                        <Button color="primary" variant="contained" onClick={handleSubmit}>
                            Guardar
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    mb={2}
                    style={{ gap: 8 }}
                >
                    <div style={{ flex: '1 1 240px', minWidth: 0, maxWidth: '100%', paddingRight: 8 }}>
                        <Typography variant="h5">Cupones</Typography>
                        <Typography variant="body2" color="textSecondary">
                            Crea y administra cupones de descuento para tus pedidos.
                        </Typography>
                    </div>
                    <Button color="primary" variant="contained" onClick={() => handleOpenDialog()}>
                        Crear cupón
                    </Button>
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Paper>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Código</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Descuento</TableCell>
                                    <TableCell>Usos</TableCell>
                                    <TableCell>Validez</TableCell>
                                    <TableCell>Activo</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {!loading &&
                                    coupons.map((coupon) => (
                                        <TableRow key={coupon.id} hover>
                                            <TableCell>{coupon.code}</TableCell>
                                            <TableCell>
                                                {coupon.discount_type === 'percentage'
                                                    ? 'Porcentaje'
                                                    : 'Monto fijo'}
                                            </TableCell>
                                            <TableCell>
                                                {coupon.discount_type === 'percentage'
                                                    ? `${coupon.discount_value}%`
                                                    : formatMoneyWithCode(coupon.discount_value)}
                                            </TableCell>
                                            <TableCell>
                                                {coupon.current_uses || 0}
                                                {coupon.max_uses
                                                    ? ` / ${coupon.max_uses}`
                                                    : ' (ilimitado)'}
                                            </TableCell>
                                            <TableCell>
                                                {coupon.valid_from
                                                    ? new Date(coupon.valid_from).toLocaleDateString()
                                                    : 'Desde ahora'}{' '}
                                                -{' '}
                                                {coupon.valid_until
                                                    ? new Date(
                                                          coupon.valid_until
                                                      ).toLocaleDateString()
                                                    : 'Sin vencimiento'}
                                            </TableCell>
                                            <TableCell>{coupon.active ? 'Sí' : 'No'}</TableCell>
                                            <TableCell align="right">
                                                <Box display="flex" justifyContent="flex-end" gap={1}>
                                                    <Button
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleOpenDialog(coupon)}
                                                    >
                                                        Editar
                                                    </Button>
                                                    <Tooltip
                                                        title={
                                                            hasAtLeastOneUse(coupon)
                                                                ? 'No se puede eliminar un cupón con usos. Desactívalo desde Editar si no quieres que se siga usando.'
                                                                : 'Eliminar cupón'
                                                        }
                                                    >
                                                        <Button
                                                            size="small"
                                                            color="secondary"
                                                            onClick={() => handleDelete(coupon)}
                                                            style={
                                                                hasAtLeastOneUse(coupon)
                                                                    ? { opacity: 0.6 }
                                                                    : undefined
                                                            }
                                                        >
                                                            Eliminar
                                                        </Button>
                                                    </Tooltip>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                {!loading && coupons.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            No hay cupones creados todavía.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {loading && (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            Cargando cupones...
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={totalRows}
                        page={page}
                        onChangePage={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onChangeRowsPerPage={handleChangeRowsPerPage}
                        rowsPerPageOptions={[10, 20, 50]}
                    />
                </Paper>
            </Grid>

            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{formValues.id ? 'Editar cupón' : 'Crear cupón'}</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Código"
                                value={formValues.code}
                                onChange={(e) => handleFormChange('code', e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel id="discount-type-label">Tipo de descuento</InputLabel>
                                <Select
                                    labelId="discount-type-label"
                                    value={formValues.discount_type}
                                    onChange={(e) =>
                                        handleFormChange('discount_type', e.target.value)
                                    }
                                >
                                    <MenuItem value="percentage">Porcentaje</MenuItem>
                                    <MenuItem value="fixed">Monto fijo</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label={
                                    formValues.discount_type === 'percentage'
                                        ? 'Descuento (%)'
                                        : 'Descuento (monto)'
                                }
                                type="number"
                                inputProps={{ min: 0.01, step: 0.01 }}
                                value={formValues.discount_value}
                                onChange={(e) =>
                                    handleFormChange('discount_value', e.target.value)
                                }
                                fullWidth
                                required
                                helperText={
                                    formValues.discount_type === 'fixed' &&
                                    formValues.discount_value
                                        ? formatMoneyWithCode(formValues.discount_value)
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Usos máximos (opcional)"
                                type="number"
                                inputProps={{ min: 1 }}
                                value={formValues.max_uses || ''}
                                onChange={(e) =>
                                    handleFormChange('max_uses', e.target.value || null)
                                }
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Monto mínimo (opcional)"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={formValues.min_order_amount || ''}
                                onChange={(e) =>
                                    handleFormChange(
                                        'min_order_amount',
                                        e.target.value || null
                                    )
                                }
                                fullWidth
                                helperText={
                                    formValues.min_order_amount
                                        ? formatMoneyWithCode(formValues.min_order_amount)
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Descuento máximo (opcional)"
                                type="number"
                                inputProps={{ min: 0, step: 0.01 }}
                                value={formValues.max_discount_amount || ''}
                                onChange={(e) =>
                                    handleFormChange(
                                        'max_discount_amount',
                                        e.target.value || null
                                    )
                                }
                                fullWidth
                                disabled={formValues.discount_type !== 'percentage'}
                                helperText={
                                    formValues.max_discount_amount
                                        ? formatMoneyWithCode(formValues.max_discount_amount)
                                        : ''
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Válido desde (opcional)"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={formValues.valid_from}
                                onChange={(e) =>
                                    handleFormChange('valid_from', e.target.value)
                                }
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Válido hasta (opcional)"
                                type="datetime-local"
                                InputLabelProps={{ shrink: true }}
                                value={formValues.valid_until}
                                onChange={(e) =>
                                    handleFormChange('valid_until', e.target.value)
                                }
                                fullWidth
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formValues.prevent_reuse_by_phone}
                                        onChange={(e) =>
                                            handleFormChange(
                                                'prevent_reuse_by_phone',
                                                e.target.checked
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label="Evitar reutilizar por teléfono"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formValues.prevent_reuse_by_email}
                                        onChange={(e) =>
                                            handleFormChange(
                                                'prevent_reuse_by_email',
                                                e.target.checked
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label="Evitar reutilizar por email"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formValues.first_purchase_only}
                                        onChange={(e) =>
                                            handleFormChange(
                                                'first_purchase_only',
                                                e.target.checked
                                            )
                                        }
                                        color="primary"
                                    />
                                }
                                label="Solo para primera compra"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formValues.active}
                                        onChange={(e) =>
                                            handleFormChange('active', e.target.checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label="Cupón activo"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button color="primary" variant="contained" onClick={handleSubmit}>
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

const mapStateToProps = ({ auth, common }) => ({
    storeId: auth.storeId,
    storeData: common.storeData,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(CouponsPage);

